import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import {
  DashboardSyndicatesEnumType,
  SuggestedSyndicatesQueryVariables,
} from '__generated__/graphql';
import { FC, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanelNew from 'components/FilterPanelNew';

import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import { industryOptions } from 'utils/constants';
import debounce from 'utils/debounce';

import { useSyndicateOnDisplay } from '../index';
import { syndicateIndustryEnumToReadable } from './options';

const SUGGESTED_SYNDICATES_QUERY = gql(`
  query SuggestedSyndicates($filters: DashboardSyndicatesFilterType, $cursor: ID, $limit: Int) {
    dashboard {
      id
      syndicates(filters: $filters, cursor: $cursor, limit: $limit) {
        nodes {
          id
          name
          image
          statistics {
            id
            topStage
            topIndustry
            dealFlow
            acceptanceStatus
          }
        }
        pageInfo {
          totalCount
          hasNextPage
          cursor
        }
      }
    }
  }
`);

const SuggestedSyndicates: FC = () => {
  const { setSyndicateOnDisplay } = useSyndicateOnDisplay();
  const { syndicateId: urlSyndicateId } = useParams();
  const [searchText, setSearchText] = useState('');
  const initialVariables: SuggestedSyndicatesQueryVariables = {
    limit: 50,
    filters: {
      type: DashboardSyndicatesEnumType.Suggested,
    },
  };

  const { loading, error, data, refetch, fetchMore, networkStatus, variables } = useQuery(
    SUGGESTED_SYNDICATES_QUERY,
    {
      variables: initialVariables,
      notifyOnNetworkStatusChange: true,
    }
  );
  const activeFiltersRef = useRef(variables?.filters);
  const debouncedSearchRef = useRef(
    debounce((text: string) => {
      refetch({
        filters: {
          ...activeFiltersRef.current,
          text: text || undefined,
        },
      });
    }, 500)
  );

  useEffect(() => {
    if (urlSyndicateId) {
      setSyndicateOnDisplay(urlSyndicateId);
    }
  }, [urlSyndicateId, setSyndicateOnDisplay]);

  useEffect(() => {
    activeFiltersRef.current = variables?.filters;
  }, [variables]);

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const dashboard = data.dashboard;

  if (!dashboard.syndicates) return <ErrorMessage title="No syndicates found" refetch={refetch} />;

  const syndicates = dashboard.syndicates.nodes;
  const pageInfo = dashboard.syndicates.pageInfo;
  const refetchSuggestedSyndicates = ({ filters, ...rest }: SuggestedSyndicatesQueryVariables) =>
    refetch({
      ...rest,
      filters: {
        ...variables?.filters,
        ...filters,
        industries: filters?.industries ? [filters.industries as never] : undefined,
      },
    });
  const filters = [
    {
      type: 'industries',
      label: 'Industry',
      options: [{ label: 'All', value: undefined }, ...industryOptions],
      readable: value => syndicateIndustryEnumToReadable(value),
    },
  ];
  const activeFilters = {
    industries: variables?.filters?.industries?.[0],
  };

  return (
    <>
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="max-w-md">
            <TextField
              placeholder="Search Syndicates"
              value={searchText}
              onChange={e => {
                const value = e.target.value;
                setSearchText(value);
                debouncedSearchRef.current(value);
              }}
            />
          </div>
        </div>
        <FilterPanelNew
          activeFilters={activeFilters}
          filters={filters}
          refetch={refetchSuggestedSyndicates}
          showSelectedFilters={false}
        />
      </div>
      <DataTable
        data={syndicates}
        onClick={syndicate => setSyndicateOnDisplay(syndicate.id)}
        columns={[
          {
            label: 'Syndicate',
            fieldName: '',
            type: 'IMAGE_WITH_NAME',
          },
          {
            label: 'Top Stage',
            fieldName: 'statistics.topStage',
            type: 'STAGE',
          },
          {
            label: 'Top Industry',
            fieldName: 'statistics.topIndustry',
            type: 'INDUSTRY',
          },
          {
            label: 'Deal Flow',
            fieldName: 'statistics.dealFlow',
            type: 'DEAL_FLOW',
          },
          {
            label: 'Accepting LPs',
            fieldName: 'statistics.acceptanceStatus',
            type: 'ACCEPTANCE_STATUS',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        filterLoading={
          networkStatus === NetworkStatus.setVariables || networkStatus === NetworkStatus.refetch
        }
        paginationLoading={loading}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
        emptyListTitle="No suggested syndicates right now"
        emptyListDescription="Check back later for new suggestions."
      />
      <div className="max-w-3xl">
        <div className="space-y-4">
          <p className="text-xs text-gray-500">
            This filtering tool and the materials on this page are provided for informational and
            educational purposes only. AngelList India and its affiliates do not provide investment
            advice to investors, and the information on this page should not be relied upon as
            research, investment advice or a recommendation of any kind. This tool filters
            syndicates based on your specified parameters. Excluded from this list are investments
            for which AngelList India does not have permission to disclose publicly.
          </p>
          <p className="text-xs text-gray-500">
            Past performance is not indicative of future returns. Examples of past investments by
            syndicates are purely for illustrative purposes. There is no guarantee that any
            syndicate will achieve the same exposure to or quality of portfolio companies held by
            any existing AngelList India syndicate funds.
          </p>
        </div>
      </div>
    </>
  );
};

export default SuggestedSyndicates;
